import HttpService from "@/services/HttpService";
import Apis from "@/services/Apis";

// Default MSO discount rule object
const defaultMsoDiscountRule = {
  name: "",
  description: "",
  discountType: "instant",
  isActive: true,
  priority: 0,
  sourceType: ["pos", "online-store", "kiosk"],
  schedule: {
    startDate: null,
    endDate: null,
    dayOfWeek: [],
    startTime: null,
    endTime: null
  },
  conditions: {
    type: "AND",
    rules: []
  },
  triggers: {
    minQuantity: null,
    minSpend: null,
    customerConditions: {
      isVeteran: null,
      visitCount: null,
      firstPurchase: null
    }
  },
  effects: [],
  stacking: {
    stackingBehavior: "default",
    excludeTypes: [],
    excludeRuleIds: [],
    onlyStackWithTypes: []
  },
  usageLimits: {
    maxPerOrder: null,
    maxPerCustomer: null,
    maxPerCustomerPerDay: null,
    maxTotalUses: null
  }
};

const state = {
  msoDiscountRules: [],
  msoDiscountRule: { ...defaultMsoDiscountRule },
  loadingStatus: {
    saveMsoDiscountRule: false,
    getMsoDiscountRules: false,
    getMsoDiscountRuleById: false,
    archiveMsoDiscountRule: false,
  },
  selectedFilter: undefined,
  selectedPreFilters: undefined,
  sort: undefined,
  pagination: { skip: 0, limit: 20 },
  initialFilter: undefined,
  filterButtons: [
    {
      isSelected: true,
      _id: 0,
      label: "All",
      name: "all",
      showAll: true,
      filter: {

      },
    },
    {
      isSelected: false,
      _id: 1,
      label: "Coupons",
      name: "coupon",
      showAll: false,
      filter: {
        discountType: "coupon",
      },
    },
    {
      isSelected: false,
      _id: 2,
      label: "Sale Pricings",
      name: "sale-pricing",
      showAll: false,
      filter: {
        discountType: "salePricing",
      },
    },
    {
      isSelected: false,
      _id: 3,
      label: "Bundles",
      name: "bundle",
      showAll: false,
      filter: {
        discountType: "bundle",
      },
    },
  ]
};

const mutations = {
  setLoadingStatus: (state, payload) => {
    if (payload) {
      state.loadingStatus[payload.api] = payload.status;
    }
  },
  setMsoDiscountRules: (state, payload) => {
    state.msoDiscountRules = payload;
  },
  setMsoDiscountRule: (state, payload) => {
    if (payload._id === "NEW") {
      state.msoDiscountRule = { ...defaultMsoDiscountRule };
    } else {
      state.msoDiscountRule = payload;
    }
  },
  setSelectedFilter: (state, payload) => {
    state.selectedFilter = payload;
  },
  setSelectedPreFilters: (state, payload) => {
    state.selectedPreFilters = payload;
  },
  updateSort: (state, payload) => {
    state.sort = payload;
  },
  updatePagination: (state, payload) => {
    state.pagination = payload;
  },
  updateInitialFilter: (state, payload) => {
    state.initialFilter = payload;
  },
  updateFilterButtons: (state, payload) => {
    state.filterButtons = payload;
  },
};

const getters = {
  msoDiscountRules: (state) => state.msoDiscountRules,
  msoDiscountRule: (state) => state.msoDiscountRule,
  loadingStatus: (state) => state.loadingStatus,
  selectedFilter: (state) => state.selectedFilter,
  selectedPreFilters: (state) => state.selectedPreFilters,
  sort: (state) => state.sort,
  pagination: (state) => state.pagination,
  initialFilter: (state) => state.initialFilter,
  filterButtons: (state) => state.filterButtons,
};

const actions = {
  saveMsoDiscountRule: ({ commit }, payload) => {
    commit("setLoadingStatus", { api: "saveMsoDiscountRule", status: true });
    HttpService.post(
      Apis.backendBaseUrl + Apis.msoDiscountRule.save,
      payload.msoDiscountRule,
      function (response, error) {
        commit("setLoadingStatus", { api: "saveMsoDiscountRule", status: false });
        if (response) {
          commit(
            "setMessage",
            { text: "Saved", type: "success" },
            { root: true }
          );
          if (payload.positiveCallback) {
            payload.positiveCallback(response.data.body);
          }
        } else {
          console.log(error);
          commit(
            "setMessage",
            { text: error.message, type: "error" },
            { root: true }
          );
          if (payload.negativeCallback) {
            payload.negativeCallback(error);
          }
        }
      }
    );
  },

  archive: ({ commit }, payload) => {
    commit("setLoadingStatus", { api: "archiveMsoDiscountRule", status: true });
    HttpService.post(
      Apis.backendBaseUrl + Apis.msoDiscountRule.archive,
      { _id: payload._id, isArchived: payload.isArchived },
      function (response, error) {
        commit("setLoadingStatus", { api: "archiveMsoDiscountRule", status: false });
        if (response) {
          const message = payload.isArchived ? "Archived" : "Restored";
          commit(
            "setMessage",
            { text: message, type: "success" },
            { root: true }
          );
          if (payload.positiveCallback) {
            payload.positiveCallback(response.data.body);
          }
        } else {
          console.log(error);
          commit(
            "setMessage",
            { text: error.message, type: "error" },
            { root: true }
          );
        }
      }
    );
  },

  getMsoDiscountRuleById: ({ commit }, payload) => {
    commit("setLoadingStatus", { api: "getMsoDiscountRuleById", status: true });
    HttpService.get(
      Apis.backendBaseUrl + Apis.msoDiscountRule.getById + "?id=" + payload._id,
      function (response, error) {
        commit("setLoadingStatus", { api: "getMsoDiscountRuleById", status: false });
        if (response) {
          commit("setMsoDiscountRule", response.data.body);
          if (payload.positiveCallback) {
            payload.positiveCallback(response.data.body);
          }
        } else {
          commit(
            "setMessage",
            { text: error.message, type: "error" },
            { root: true }
          );
          console.log(error);
          if (payload.negativeCallback) {
            payload.negativeCallback(error);
          }
        }
      }
    );
  },

  toggleActive: ({ commit }, payload) => {
    HttpService.post(
      Apis.backendBaseUrl + Apis.msoDiscountRule.toggleActive,
      { _id: payload._id },
      function (response, error) {
        if (response) {
          commit(
            "setMessage",
            { text: "Status updated", type: "success" },
            { root: true }
          );
          if (payload.positiveCallback) {
            payload.positiveCallback(response.data.body);
          }
        } else {
          console.log(error);
          commit(
            "setMessage",
            { text: error.message, type: "error" },
            { root: true }
          );
        }
      }
    );
  },

  setSelectedFilter: ({ commit }, payload) => {
    commit("setSelectedFilter", payload);
  },
  setSelectedPreFilters: ({ commit }, payload) => {
    commit("setSelectedPreFilters", payload);
  },
  updateSort: ({ commit }, payload) => {
    commit("updateSort", payload);
  },
  updatePagination: ({ commit }, payload) => {
    commit("updatePagination", payload);
  },
  updateInitialFilter: ({ commit }, payload) => {
    commit("updateInitialFilter", payload);
  },
  updateFilterButtons: ({ commit }, payload) => {
    commit("updateFilterButtons", payload);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};

